import React, { useState, useEffect, useCallback } from "react";
import {
  FaEye,
  FaEdit,
  FaCheck,
  FaTimes,
  FaSpinner,
  FaFilter,
  FaCalendarAlt,
  FaUser,
  FaMoneyBillWave,
  FaSearch,
  FaTrash,
} from "react-icons/fa";
import { useNotification } from "../../contexts/NotificationContext";
import {
  newFinanceService,
  formatCurrencyTotals,
} from "../../services/newFinance.service";
import { memberService } from "../../services/member.service";
import type {
  FinanceRecord,
  FinanceStatus,
  SearchableOfferingType,
} from "../../types/newFinance";
import { OFFERING_SEARCH_OPTIONS } from "../../types/newFinance";
import type { RegisteredMember } from "../../types/member";
import { FinanceDetailModal } from "./FinanceDetailModal";
import { FinanceInputModal } from "./FinanceInputModal";
import { PersonalOfferingModal } from "./PersonalOfferingModal";
import { AutocompleteInput } from "../common/AutocompleteInput";
import { usePagination } from "../../hooks/usePagination";
import { AdvancedPagination } from "../common/AdvancedPagination";
import type { FinanceFormData } from "../../types/newFinance";

interface Props {
  onEdit?: (record: FinanceRecord) => void;
}

export const FinanceList: React.FC<Props> = ({ onEdit }) => {
  const { addNotification } = useNotification();

  // 상태 관리
  const [records, setRecords] = useState<FinanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // 필터 상태
  const [statusFilter, setStatusFilter] = useState<FinanceStatus | "all">(
    "all",
  );
  const [dateRange, setDateRange] = useState({
    start: "",
    end: "",
  });

  // 새로운 검색 상태
  const [nameSearch, setNameSearch] = useState("");
  const [offeringTypeFilter, setOfferingTypeFilter] = useState<
    SearchableOfferingType | "all"
  >("all");
  const [members, setMembers] = useState<RegisteredMember[]>([]);

  // 모달 상태
  const [selectedRecord, setSelectedRecord] = useState<FinanceRecord | null>(
    null,
  );
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showInputModal, setShowInputModal] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [showPersonalModal, setShowPersonalModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPersonName, setSelectedPersonName] = useState("");
  const [editReason, setEditReason] = useState("");
  const [pendingFormData, setPendingFormData] =
    useState<FinanceFormData | null>(null);

  // 승인 상태별 레코드 분리
  const pendingRecords = records.filter(
    (record) => record.status !== "approved",
  );
  const approvedRecords = records.filter(
    (record) => record.status === "approved",
  );

  // 페이지네이션
  const pendingPagination = usePagination(pendingRecords, {
    initialPageSize: 5,
    pageSizeOptions: [5, 10, 20],
  });

  const approvedPagination = usePagination(approvedRecords, {
    initialPageSize: 5,
    pageSizeOptions: [5, 10, 20],
  });

  // 성도 목록 로드 (자동완성용)
  useEffect(() => {
    const loadMembers = async () => {
      try {
        const memberData = await memberService.getAllRegisteredMembers();
        setMembers(memberData);
      } catch (error) {
        console.error("성도 목록 로딩 오류:", error);
      }
    };

    loadMembers();
  }, []);

  useEffect(() => {
    loadRecords();
  }, [statusFilter, nameSearch, offeringTypeFilter]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      let data: FinanceRecord[];

      // 기본 검색
      if (statusFilter === "all") {
        data = await newFinanceService.getAllFinanceRecords();
      } else {
        data = await newFinanceService.getFinanceRecordsByStatus(statusFilter);
      }

      // 날짜 필터 적용
      if (dateRange.start || dateRange.end) {
        data = data.filter((record) => {
          const recordDate = record.date;
          if (dateRange.start && recordDate < dateRange.start) return false;
          if (dateRange.end && recordDate > dateRange.end) return false;
          return true;
        });
      }

      // 프론트엔드에서 전체 검색 적용
      if (nameSearch || offeringTypeFilter !== "all") {
        data = data.filter((record) => {
          let matchesSearch = true;

          // 이름 검색 (전체 내역에서)
          if (nameSearch) {
            const searchTerm = nameSearch.toLowerCase();
            matchesSearch = false;

            // description 필드 검색
            if (
              record.description &&
              record.description.toLowerCase().includes(searchTerm)
            ) {
              matchesSearch = true;
            }

            // titheItems 배열 내부 검색
            if (record.titheItems) {
              for (const item of record.titheItems) {
                if (item.name && item.name.toLowerCase().includes(searchTerm)) {
                  matchesSearch = true;
                  break;
                }
                if (
                  item.description &&
                  item.description.toLowerCase().includes(searchTerm)
                ) {
                  matchesSearch = true;
                  break;
                }
                if (
                  item.amount &&
                  item.amount.toString().includes(searchTerm)
                ) {
                  matchesSearch = true;
                  break;
                }
              }
            }

            // thanksgivingItems 배열 내부 검색
            if (record.thanksgivingItems) {
              for (const item of record.thanksgivingItems) {
                if (item.name && item.name.toLowerCase().includes(searchTerm)) {
                  matchesSearch = true;
                  break;
                }
                if (
                  item.description &&
                  item.description.toLowerCase().includes(searchTerm)
                ) {
                  matchesSearch = true;
                  break;
                }
                if (
                  item.amount &&
                  item.amount.toString().includes(searchTerm)
                ) {
                  matchesSearch = true;
                  break;
                }
              }
            }

            // otherIncomeItems 배열 내부 검색
            if (record.otherIncomeItems) {
              for (const item of record.otherIncomeItems) {
                if (item.name && item.name.toLowerCase().includes(searchTerm)) {
                  matchesSearch = true;
                  break;
                }
                if (
                  item.description &&
                  item.description.toLowerCase().includes(searchTerm)
                ) {
                  matchesSearch = true;
                  break;
                }
                if (
                  item.amount &&
                  item.amount.toString().includes(searchTerm)
                ) {
                  matchesSearch = true;
                  break;
                }
              }
            }

            // expenseItems 배열 내부 검색
            if (record.expenseItems) {
              for (const item of record.expenseItems) {
                if (
                  item.description &&
                  item.description.toLowerCase().includes(searchTerm)
                ) {
                  matchesSearch = true;
                  break;
                }
                if (
                  item.amount &&
                  item.amount.toString().includes(searchTerm)
                ) {
                  matchesSearch = true;
                  break;
                }
              }
            }
          }

          // 헌금 종류 필터 적용
          if (offeringTypeFilter !== "all") {
            let hasOfferingType = false;

            switch (offeringTypeFilter) {
              case "tithe":
                hasOfferingType =
                  record.titheItems && record.titheItems.length > 0;
                break;
              case "thanksgiving":
                hasOfferingType =
                  record.thanksgivingItems &&
                  record.thanksgivingItems.length > 0;
                break;
              case "sunday":
              case "friday":
              case "mission":
              case "seasonal":
              case "building":
              case "purpose":
              case "other":
                hasOfferingType =
                  record.otherIncomeItems &&
                  record.otherIncomeItems.some(
                    (item) => item.category === offeringTypeFilter,
                  );
                break;
            }

            matchesSearch = matchesSearch && hasOfferingType;
          }

          return matchesSearch;
        });
      }

      setRecords(data);
    } catch (error) {
      console.error("재정 데이터 로딩 오류:", error);
      addNotification("재정 데이터를 불러올 수 없습니다.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    setActionLoading(id);
    try {
      await newFinanceService.approveFinanceRecord(id);
      addNotification("재정 데이터가 승인되었습니다.", "success");
      await loadRecords();
    } catch (error) {
      console.error("승인 오류:", error);
      addNotification("승인 처리 중 오류가 발생했습니다.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDetail = (record: FinanceRecord) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  const handleEdit = (record: FinanceRecord) => {
    console.log("🎯 수정 버튼 클릭 - 직접 FinanceInputModal 열기");
    setSelectedRecord(record);
    setShowInputModal(true);
  };

  const handlePersonalView = (name: string) => {
    setSelectedPersonName(name);
    setShowPersonalModal(true);
  };

  const handleDelete = (record: FinanceRecord) => {
    setSelectedRecord(record);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRecord) return;

    setActionLoading(selectedRecord.id);
    try {
      await newFinanceService.deleteFinanceRecord(selectedRecord.id);
      addNotification("재정 데이터가 삭제되었습니다.", "success");
      await loadRecords();
      setShowDeleteModal(false);
      setSelectedRecord(null);
    } catch (error) {
      console.error("삭제 오류:", error);
      addNotification("삭제 처리 중 오류가 발생했습니다.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  // 원본 데이터를 FinanceFormData 형태로 변환
  const getFormDataFromRecord = (record: FinanceRecord): FinanceFormData => {
    return {
      date: record.date,
      titheItems: record.titheItems?.map(({ id, ...item }) => item) || [],
      thanksgivingItems:
        record.thanksgivingItems?.map(({ id, ...item }) => item) || [],
      otherIncomeItems:
        record.otherIncomeItems?.map(({ id, ...item }) => item) || [],
      expenseItems: record.expenseItems?.map(({ id, ...item }) => item) || [],
    };
  };

  // 수정된 데이터 처리
  const handleFormSubmit = (formData: FinanceFormData) => {
    setPendingFormData(formData);
    setShowInputModal(false);
    setShowReasonModal(true);
  };

  // 수정 사유 입력 후 최종 저장
  const handleReasonSubmit = useCallback(async () => {
    console.log("🔍 수정 사유 검증 시작");

    // DOM에서 직접 현재 값 가져오기 (React state 동기화 문제 방지)
    const textarea = document.querySelector("textarea") as HTMLTextAreaElement;
    const currentReason = textarea?.value?.trim() || "";

    console.log("📋 상태 및 DOM 값 검증:", {
      selectedRecord: !!selectedRecord,
      pendingFormData: !!pendingFormData,
      stateReason: editReason,
      domReason: currentReason,
      stateReasonTrimmed: editReason.trim(),
      domReasonTrimmed: currentReason,
      textareaFound: !!textarea,
    });

    // DOM 값으로 검증
    if (!selectedRecord || !pendingFormData || !currentReason) {
      console.log("❌ 검증 실패:", {
        hasSelectedRecord: !!selectedRecord,
        hasPendingFormData: !!pendingFormData,
        hasCurrentReason: !!currentReason,
        actualDomReason: `"${currentReason}"`,
      });
      addNotification("수정 사유를 입력해주세요.", "warning");
      return;
    }

    console.log("✅ 검증 성공 - 저장 진행 중...");
    setActionLoading(selectedRecord.id);

    try {
      // DOM에서 가져온 값으로 저장
      await newFinanceService.updateFinanceRecord(
        selectedRecord.id,
        pendingFormData,
        currentReason,
      );

      // 성공 메시지
      const dateObj = new Date(selectedRecord.date);
      const formattedDate = dateObj.toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      addNotification(
        `${formattedDate} 재정내역 수정이 완료되었습니다. 최고관리자에게 승인을 요청하겠습니다. ✅`,
        "success",
      );

      console.log("💾 수정 완료 - 목록 새로고침 중...");

      // 목록 새로고침 완료 후 모달 닫기
      await loadRecords();

      console.log("📋 목록 새로고침 완료 - 모달 닫기");

      // 모달 닫기 및 상태 초기화
      setShowReasonModal(false);
      setSelectedRecord(null);
      setPendingFormData(null);
      setEditReason("");
    } catch (error) {
      console.error("수정 오류:", error);
      addNotification("수정 처리 중 오류가 발생했습니다.", "error");
    } finally {
      setActionLoading(null);
    }
  }, [selectedRecord, pendingFormData, addNotification, loadRecords]);

  const getStatusBadge = (status: FinanceStatus) => {
    const styles = {
      pending:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
      approved:
        "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
      reapproval:
        "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
    };

    const labels = {
      pending: "승인대기",
      approved: "승인완료",
      reapproval: "수정요청 승인건",
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}
      >
        {labels[status]}
      </span>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
      {/* 헤더 및 필터 */}
      <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              📋 재정 목록 관리
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 [word-break:keep-all]">
              재정 내역을 조회하고 수정하거나 승인합니다.
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 sm:hidden">목록은 좌우로 밀어서 전체 내용을 볼 수 있습니다.</p>
          </div>

          {/* 필터 컨트롤 */}
          <div className="flex flex-col gap-4">
            {/* 검색 필터 첫 번째 행 */}
            <div className="flex flex-col lg:flex-row gap-3">
              {/* 이름 검색 */}
              <div className="flex items-center space-x-2 min-w-0 flex-1">
                <FaSearch className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <AutocompleteInput
                  options={members.map((member) => ({
                    value: member.name,
                    label: member.name,
                  }))}
                  value={nameSearch}
                  onChange={setNameSearch}
                  placeholder="이름으로 검색..."
                  className="min-w-0 flex-1"
                />
              </div>

              {/* 헌금 종류 검색 */}
              <div className="flex items-center space-x-2">
                <FaMoneyBillWave className="h-4 w-4 text-gray-400" />
                <select
                  aria-label="헌금 종류"
                  value={offeringTypeFilter}
                  onChange={(e) =>
                    setOfferingTypeFilter(
                      e.target.value as SearchableOfferingType | "all",
                    )
                  }
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  {OFFERING_SEARCH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* 초기화 버튼 */}
              {(nameSearch || offeringTypeFilter !== "all") && (
                <button
                  onClick={() => {
                    setNameSearch("");
                    setOfferingTypeFilter("all");
                  }}
                  className="px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center space-x-1"
                  title="검색 초기화"
                >
                  <FaTimes className="h-3 w-3" />
                  <span className="hidden sm:inline">초기화</span>
                </button>
              )}
            </div>

            {/* 기존 필터 두 번째 행 */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center space-x-2">
                <FaFilter className="h-4 w-4 text-gray-400" />
                <select
                  aria-label="승인 상태"
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as FinanceStatus | "all")
                  }
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="all">전체</option>
                  <option value="pending">승인대기</option>
                  <option value="approved">승인완료</option>
                  <option value="reapproval">재승인요청</option>
                </select>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <FaCalendarAlt className="h-4 w-4 text-gray-400" />
                <input
                  aria-label="조회 시작 날짜"
                  type="date"
                  value={dateRange.start}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, start: e.target.value }))
                  }
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
                <span className="text-gray-400">~</span>
                <input
                  aria-label="조회 종료 날짜"
                  type="date"
                  value={dateRange.end}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, end: e.target.value }))
                  }
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
                <button
                  onClick={loadRecords}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                >
                  적용
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 목록 내용 */}
      <div className="p-4 sm:p-6">
        {/* 검색 결과 정보 */}
        {!loading && (nameSearch || offeringTypeFilter !== "all") && (
          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FaSearch className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <span className="text-blue-800 dark:text-blue-200 font-medium">
                  검색 결과: {records.length}건
                </span>
              </div>
              <div className="text-sm text-blue-600 dark:text-blue-400">
                {nameSearch && (
                  <button
                    onClick={() => handlePersonalView(nameSearch)}
                    className="bg-blue-200 dark:bg-blue-800 hover:bg-blue-300 dark:hover:bg-blue-700 px-2 py-1 rounded mr-2 transition-colors cursor-pointer"
                    title="개인 헌금 내역 보기"
                  >
                    이름: {nameSearch} 👁️
                  </button>
                )}
                {offeringTypeFilter !== "all" && (
                  <span className="bg-blue-200 dark:bg-blue-800 px-2 py-1 rounded">
                    헌금:{" "}
                    {
                      OFFERING_SEARCH_OPTIONS.find(
                        (o) => o.value === offeringTypeFilter,
                      )?.label
                    }
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <FaSpinner className="h-8 w-8 animate-spin text-blue-500" />
            <span className="ml-2 text-gray-600 dark:text-gray-400">
              데이터 로딩 중...
            </span>
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <FaMoneyBillWave className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>해당 조건의 재정 데이터가 없습니다.</p>
            <p className="text-sm mt-2">다른 필터 조건을 시도해보세요.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* 승인 대기 내역 */}
            {pendingRecords.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-yellow-600 dark:text-yellow-400 flex items-center">
                    <span className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></span>
                    승인 대기 내역 ({pendingRecords.length}건)
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-yellow-50 dark:bg-yellow-900/20">
                      <tr>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          날짜
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          상태
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          파일명
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          작성자
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          관리
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {pendingPagination.paginatedData.map((record) => (
                        <tr
                          key={record.id}
                          className="hover:bg-yellow-50 dark:hover:bg-yellow-900/10"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-900 dark:text-white">
                            {record.date}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {getStatusBadge(record.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-blue-600 dark:text-blue-400 font-medium">
                            {`${new Date(record.date).getFullYear()}년 ${new Date(record.date).getMonth() + 1}월 ${new Date(record.date).getDate()}일 재정현황`}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex flex-col items-center">
                              <div className="flex items-center">
                                <FaUser className="h-3 w-3 mr-1" />
                                {record.createdBy}
                              </div>
                              <div className="text-xs text-gray-400">
                                {record.createdAt.toLocaleDateString()}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex justify-center items-center space-x-2">
                              {/* 상세보기 */}
                              <button
                                onClick={() => handleDetail(record)}
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                title="상세보기"
                                aria-label={`${record.date} 상세보기`}
                              >
                                <FaEye className="h-4 w-4" />
                                <span className="text-xs">상세</span>
                              </button>

                              {/* 수정 */}
                              <button
                                onClick={() => handleEdit(record)}
                                className="text-yellow-600 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-300"
                                title="수정"
                                aria-label={`${record.date} 수정`}
                              >
                                <FaEdit className="h-4 w-4" />
                                <span className="text-xs">수정</span>
                              </button>

                              {/* 삭제 */}
                              <button
                                onClick={() => handleDelete(record)}
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                title="삭제"
                                aria-label={`${record.date} 삭제`}
                              >
                                <FaTrash className="h-4 w-4" />
                                <span className="text-xs">삭제</span>
                              </button>

                              {/* 승인 (승인대기 또는 재승인요청인 경우만) */}
                              {(record.status === "pending" ||
                                record.status === "reapproval") && (
                                <button
                                  onClick={() => {
                                    if (window.confirm(`${record.date} 재정 내역을 승인하시겠습니까? 상세 내용을 확인한 뒤 승인해 주세요.`)) {
                                      void handleApprove(record.id);
                                    }
                                  }}
                                  disabled={actionLoading === record.id}
                                  className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                                  title="승인"
                                  aria-label={`${record.date} 승인`}
                                >
                                  {actionLoading === record.id ? (
                                    <FaSpinner className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <FaCheck className="h-4 w-4" />
                                  )}
                                  <span className="text-xs">승인</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 승인 대기 목록 페이지네이션 */}
                {pendingRecords.length > 0 && (
                  <AdvancedPagination
                    currentPage={pendingPagination.currentPage}
                    totalItems={pendingPagination.totalItems}
                    pageSize={pendingPagination.pageSize}
                    onPageChange={pendingPagination.setCurrentPage}
                    onPageSizeChange={pendingPagination.setPageSize}
                    pageSizeOptions={[5, 10, 20]}
                    showInfo={true}
                  />
                )}
              </div>
            )}

            {/* 승인 완료 내역 */}
            {approvedRecords.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-green-600 dark:text-green-400 flex items-center">
                    <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                    승인 완료 내역 ({approvedRecords.length}건)
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-green-50 dark:bg-green-900/20">
                      <tr>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          날짜
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          상태
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          파일명
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          작성자
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          관리
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {approvedPagination.paginatedData.map((record) => (
                        <tr
                          key={record.id}
                          className="hover:bg-green-50 dark:hover:bg-green-900/10"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-900 dark:text-white">
                            {record.date}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {getStatusBadge(record.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-blue-600 dark:text-blue-400 font-medium">
                            {`${new Date(record.date).getFullYear()}년 ${new Date(record.date).getMonth() + 1}월 ${new Date(record.date).getDate()}일 재정현황`}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex flex-col items-center">
                              <div className="flex items-center">
                                <FaUser className="h-3 w-3 mr-1" />
                                {record.createdBy}
                              </div>
                              <div className="text-xs text-gray-400">
                                {record.createdAt.toLocaleDateString()}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex justify-center items-center space-x-2">
                              {/* 상세보기 */}
                              <button
                                onClick={() => handleDetail(record)}
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                title="상세보기"
                                aria-label={`${record.date} 상세보기`}
                              >
                                <FaEye className="h-4 w-4" />
                                <span className="text-xs">상세</span>
                              </button>

                              {/* 수정 */}
                              <button
                                onClick={() => handleEdit(record)}
                                className="text-yellow-600 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-300"
                                title="수정"
                                aria-label={`${record.date} 수정`}
                              >
                                <FaEdit className="h-4 w-4" />
                                <span className="text-xs">수정</span>
                              </button>

                              {/* 삭제 */}
                              <button
                                onClick={() => handleDelete(record)}
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                title="삭제"
                                aria-label={`${record.date} 삭제`}
                              >
                                <FaTrash className="h-4 w-4" />
                                <span className="text-xs">삭제</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 승인 완료 목록 페이지네이션 */}
                {approvedRecords.length > 0 && (
                  <AdvancedPagination
                    currentPage={approvedPagination.currentPage}
                    totalItems={approvedPagination.totalItems}
                    pageSize={approvedPagination.pageSize}
                    onPageChange={approvedPagination.setCurrentPage}
                    onPageSizeChange={approvedPagination.setPageSize}
                    pageSizeOptions={[5, 10, 20]}
                    showInfo={true}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 상세보기 모달 */}
      {selectedRecord && (
        <FinanceDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedRecord(null);
          }}
          record={selectedRecord}
        />
      )}

      {/* 재정 입력 모달 (수정용) */}
      {selectedRecord && (
        <FinanceInputModal
          isOpen={showInputModal}
          onClose={() => {
            setShowInputModal(false);
            // 수정 모드에서는 수정 사유 입력 완료 후에만 상태 초기화
            // setSelectedRecord(null);
            // setPendingFormData(null);
          }}
          selectedDate={selectedRecord.date}
          onSuccess={handleFormSubmit}
          editMode={true}
          initialData={getFormDataFromRecord(selectedRecord)}
        />
      )}

      {/* 개인별 헌금 내역 모달 */}
      <PersonalOfferingModal
        isOpen={showPersonalModal}
        onClose={() => {
          setShowPersonalModal(false);
          setSelectedPersonName("");
        }}
        personName={selectedPersonName}
      />

      {/* 수정 사유 입력 모달 */}
      {showReasonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full">
            {/* 헤더 */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FaEdit className="h-6 w-6 text-white mr-3" />
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      수정 사유 입력
                    </h2>
                    <p className="text-orange-100">
                      수정하신 이유를 입력해주세요
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowReasonModal(false);
                    setPendingFormData(null);
                    setEditReason("");
                  }}
                  className="text-white hover:text-orange-200 transition-colors"
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* 본문 */}
            <div className="p-6">
              <div className="space-y-4">
                {/* 수정 날짜 정보 */}
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                  <h3 className="font-medium text-orange-800 dark:text-orange-200 mb-2">
                    수정할 재정 데이터
                  </h3>
                  <div className="text-sm text-orange-700 dark:text-orange-300 space-y-1">
                    <p>📅 날짜: {selectedRecord?.date}</p>
                    <p>👤 작성자: {selectedRecord?.createdBy}</p>
                  </div>
                </div>

                {/* 수정 사유 입력 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    수정 사유 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={editReason}
                    onChange={(e) => setEditReason(e.target.value)}
                    placeholder="수정하는 이유를 상세히 설명해주세요..."
                    rows={4}
                    lang="ko-KR"
                    autoComplete="off"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-white resize-none"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    이 내용은 감사 기록으로 저장됩니다.
                  </p>
                </div>
              </div>
            </div>

            {/* 푸터 */}
            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 rounded-b-2xl">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowReasonModal(false);
                    setPendingFormData(null);
                    setEditReason("");
                  }}
                  className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  disabled={actionLoading === selectedRecord?.id}
                >
                  취소
                </button>
                <button
                  onClick={handleReasonSubmit}
                  disabled={actionLoading === selectedRecord?.id}
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center"
                >
                  {actionLoading === selectedRecord?.id ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" />
                      수정 중...
                    </>
                  ) : (
                    <>
                      <FaCheck className="mr-2" />
                      수정 완료
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteModal && selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full">
            {/* 헤더 */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FaTrash className="h-6 w-6 text-white mr-3" />
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      재정 데이터 삭제
                    </h2>
                    <p className="text-red-100">정말로 삭제하시겠습니까?</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedRecord(null);
                  }}
                  className="text-white hover:text-red-200 transition-colors"
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* 본문 */}
            <div className="p-6">
              <div className="space-y-4">
                {/* 경고 메시지 */}
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border-l-4 border-red-500">
                  <div className="flex items-start">
                    <FaTrash className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 mr-3" />
                    <div>
                      <h3 className="font-medium text-red-800 dark:text-red-200 mb-1">
                        ⚠️ 주의: 이 작업은 되돌릴 수 없습니다
                      </h3>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        삭제된 데이터는 복구할 수 없습니다. 신중하게
                        결정해주세요.
                      </p>
                    </div>
                  </div>
                </div>

                {/* 삭제할 데이터 정보 */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-800 dark:text-gray-200 mb-2">
                    삭제할 재정 데이터
                  </h3>
                  <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                    <p>📅 날짜: {selectedRecord.date}</p>
                    <p>👤 작성자: {selectedRecord.createdBy}</p>
                    <p>
                      📊 상태:{" "}
                      {selectedRecord.status === "pending"
                        ? "승인대기"
                        : selectedRecord.status === "approved"
                          ? "승인완료"
                          : "재승인요청"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 푸터 */}
            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 rounded-b-2xl">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedRecord(null);
                  }}
                  className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  disabled={actionLoading === selectedRecord.id}
                >
                  취소
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={actionLoading === selectedRecord.id}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center"
                >
                  {actionLoading === selectedRecord.id ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" />
                      삭제 중...
                    </>
                  ) : (
                    <>
                      <FaTrash className="mr-2" />
                      삭제 확인
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
